import classNames from "classnames";
import { RefObject, useEffect, useRef, useState } from "react";
import {
  GroupedNotes,
  MidiNote,
  midiNotes,
  midiToNote,
  Note,
  noteToMidi,
} from "./constants/notes";
import {
  isNoteWithinMidiRange,
  isNoteWithinRange,
  middleMidiNoteOfRange,
  predefinedRanges,
  PredefinedRangeType,
  Range,
  rangeToMidi,
} from "./constants/ranges";
import { Player } from "./usePlayer/usePlayer";
import Button from "ui/atoms/Button/Button";

interface Props {
  midiNotes: GroupedNotes[];
  player: Player;
}

const widthClassName = "w-12";
const widthPixel = 48;
const marginRightClassName = "mr-1";
const marginRightPixel = 4;

export default function PianoRange({ midiNotes, player }: Props) {
  const range = player.state.range;
  const setRange = player.setRange;
  const [internalLeft, setInternalLeft] = useState<Note | undefined>(undefined);
  const [hoveredNote, setHoveredNote] = useState<MidiNote | null>(null);
  const scrollElementRef = useRef<HTMLDivElement>(null);

  useScrollToNoteOnInit(getScrollToNote(player), scrollElementRef);

  useEffect(() => {
    // this means it was changed from elsewhere so reset the internal state
    if (internalLeft && range?.low !== internalLeft) {
      setInternalLeft(undefined);
    }
  }, [internalLeft, range?.low]);

  const onNoteSelect = (midiNote: MidiNote) => {
    const note = midiToNote(midiNote);
    const currentRangeLowMidi = range ? noteToMidi(range.low) : midiNote;

    // Begin the selection by setting the left note and updating the range to a single note
    if (!internalLeft || midiNote <= currentRangeLowMidi) {
      // set both to the same note
      setRange({
        range: "custom",
        low: note,
        high: note,
      });

      // keep track of the note so we know to update right on next click
      setInternalLeft(note);
    } else {
      // complete the range select
      setRange({
        range: "custom",
        low: internalLeft,
        high: note,
      });

      // reset the internal state
      setInternalLeft(undefined);
    }
  };

  function isNoteWithinHoverRange(midiNote: MidiNote) {
    if (!internalLeft) {
      return false;
    }

    if (!hoveredNote) {
      return false;
    }

    const low = noteToMidi(internalLeft);
    const high = hoveredNote;

    return isNoteWithinMidiRange(midiNote, [low, high]);
  }

  return (
    <div className={"overflow-auto"} ref={scrollElementRef}>
      <div
        className={classNames("whitespace-nowrap relative")}
        onMouseLeave={() => setHoveredNote(null)}
      >
        <RangeMarkers
          ranges={[
            PredefinedRangeType.Soprano,
            PredefinedRangeType.MezzoSoprano,
            PredefinedRangeType.Alto,
            PredefinedRangeType.Tenor,
            PredefinedRangeType.Baritone,
            PredefinedRangeType.Bass,
          ]}
          className="mb-4"
          midiNotes={midiNotes}
          setRange={setRange}
        />

        {midiNotes.map(([whiteKey, blackKey]) => {
          const whiteKeyInRange =
            whiteKey && range ? isNoteWithinRange(whiteKey, range) : false;
          const whiteKeyInHoverRange = whiteKey
            ? hoveredNote && isNoteWithinHoverRange(whiteKey)
            : false;
          const blackKeyInRange =
            blackKey && range ? isNoteWithinRange(blackKey, range) : false;
          const blackKeyInHoverRange = blackKey
            ? hoveredNote && isNoteWithinHoverRange(blackKey)
            : false;

          return (
            <div
              key={`${whiteKey}${blackKey}`}
              className={classNames(
                "relative inline-block font-brik text-center",
                widthClassName,
                marginRightClassName
              )}
            >
              {blackKey && (
                <div
                  {...makeMidiNoteDataAttr(blackKey)}
                  className={classNames(
                    "top-0 rounded border-2 py-2 mb-1 relative left-6 cursor-pointer transition-all",
                    widthClassName,
                    marginRightClassName,
                    blackKeyInRange || blackKeyInHoverRange
                      ? "border-purple-500 text-purple-500"
                      : "border-white text-white hover:border-purple-300 hover:text-purple-300 "
                  )}
                  onClick={() => player.playMelody([blackKey])}
                >
                  <div>{midiToNote(blackKey)}</div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button
                      theme="white"
                      active={blackKeyInRange}
                      onClick={() => onNoteSelect(blackKey)}
                      onMouseEnter={() => setHoveredNote(blackKey)}
                    />
                  </div>
                </div>
              )}

              {whiteKey && (
                <div
                  {...makeMidiNoteDataAttr(whiteKey)}
                  className={classNames(
                    "relative rounded flex flex-col justify-between py-2 cursor-pointer transition-all",
                    whiteKeyInRange || whiteKeyInHoverRange
                      ? "bg-purple-500 text-white"
                      : "bg-white text-black hover:bg-purple-400 hover:text-white"
                  )}
                  onClick={() => player.playMelody([whiteKey])}
                >
                  <div>{midiToNote(whiteKey)}</div>
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button
                      theme="white"
                      active={whiteKeyInRange}
                      onClick={() => onNoteSelect(whiteKey)}
                      onMouseEnter={() => setHoveredNote(whiteKey)}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RangeMarkers({
  ranges,
  className,
  midiNotes,
  setRange,
}: {
  ranges: PredefinedRangeType[];
  className?: string;
  midiNotes: GroupedNotes[];
  setRange: (range: Range) => void;
}) {
  function getRangeStyle(rangeType: PredefinedRangeType): React.CSSProperties {
    const range = predefinedRanges[rangeType];

    if (!range) {
      return {};
    }

    const [start, end] = rangeToMidi(range);

    const numFromStart = midiNotes.findIndex(([whiteKey, blackKey]) => {
      return whiteKey === start || blackKey === start;
    });

    const numFromEnd = midiNotes.findIndex(([whiteKey, blackKey]) => {
      return whiteKey === end || blackKey === end;
    });

    const left = numFromStart * widthPixel + numFromStart * marginRightPixel;
    const width =
      (numFromEnd - numFromStart + 1) * widthPixel +
      (numFromEnd - numFromStart) * marginRightPixel;

    return {
      left,
      width,
    };
  }

  function makeRangeMarker(rangeType: PredefinedRangeType, index: number) {
    const theme = (() => {
      const themes: { line: string; text: string }[] = [
        { line: "bg-blue-500 ", text: "text-blue-500 hover:text-blue-300" },
        {
          line: "bg-yellow-500",
          text: "text-yellow-500 hover:text-yellow-300",
        },
        { line: "bg-red-500", text: "text-red-500 hover:text-red-300" },
        { line: "bg-green-500", text: "text-green-500 hover:text-green-300" },
        {
          line: "bg-purple-500",
          text: "text-purple-500 hover:text-purple-300",
        },
      ];

      return themes[index % themes.length];
    })();

    return (
      <div
        className={"relative block text-left "}
        style={getRangeStyle(rangeType)}
        key={rangeType}
      >
        <div
          className={classNames(
            "left-0 sticky leading-none font-brik cursor-pointer inline-block text-xl",
            theme.text
          )}
          onClick={() => setRange(predefinedRanges[rangeType])}
        >
          {rangeType}

          <div className={"absolute inset-0 scale-150"}></div>
        </div>

        <div
          className={classNames(
            "absolute left-0 right-0 top-5 h-1 pointer-events-none",
            theme.line
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(className)}
      style={{ paddingBottom: 4 * ranges.length }}
    >
      {ranges.map((rangeType, i) => makeRangeMarker(rangeType, i))}
    </div>
  );
}

function useScrollToNoteOnInit(
  midiNote: MidiNote,
  pianoScrollContainerRef: RefObject<HTMLDivElement>
) {
  const once = useRef(false);
  useEffect(() => {
    const el = document.querySelector(`[${dataAttr}="${midiNote}"]`);
    const pianoScrollContainer = pianoScrollContainerRef.current;

    if (el && pianoScrollContainer) {
      if (once.current) {
        return;
      }

      once.current = true;

      const elWidth = el.getBoundingClientRect().width;
      const containerWidth = pianoScrollContainer.getBoundingClientRect().width;

      // Calculate the position to scroll to for centering el within pianoScrollContainer
      const elLeft = el.getBoundingClientRect().left;
      const containerLeft = pianoScrollContainer.getBoundingClientRect().left;
      const scrollLeft =
        elLeft -
        containerLeft -
        containerWidth / 2 +
        elWidth / 2 +
        pianoScrollContainer.scrollLeft;

      console.log({ scrollLeft, elLeft, containerLeft });

      // Scroll el to the center of the pianoScrollContainer without animation
      pianoScrollContainer.scrollLeft = scrollLeft;
    }
  }, [midiNote, pianoScrollContainerRef]);
}

function makeMidiNoteDataAttr(midiNote: MidiNote) {
  return { [dataAttr]: midiNote };
}

const dataAttr = "data-midi-note";

/**
 * If no range scroll to middle of all notes or the middle of the range
 */

function getScrollToNote(player: Player): number {
  if (!player.state.range) {
    return noteToMidi(predefinedRanges.soprano.low);
  }

  return middleMidiNoteOfRange(player.state.range);
}
